import { useState, useEffect } from 'react';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Coins, Plus, ArrowUp, ArrowDown, ArrowCounterClockwise } from '@phosphor-icons/react';

export default function CreditManagementPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creditOpen, setCreditOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [credits, setCredits] = useState('');
  const [history, setHistory] = useState([]);
  const [historyAdmin, setHistoryAdmin] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Admin's own summary
  const [mySummary, setMySummary] = useState(null);

  const fetchData = () => {
    if (user?.role === 'super_admin') {
      API.get('/admins').then(r => setAdmins(r.data.admins || [])).catch(() => {});
      API.get('/credits/summary').then(r => setSummaries(r.data.summaries || [])).catch(() => {});
    } else {
      API.get('/credits/summary').then(r => setMySummary(r.data)).catch(() => {});
    }
    setLoading(false);
  };
  useEffect(fetchData, [user]);

  const openAddCredits = (admin) => {
    setSelectedAdmin(admin);
    setCredits('');
    setCreditOpen(true);
  };

  const addCredits = async (e) => {
    e.preventDefault();
    try {
      await API.post(`/admins/${selectedAdmin.id}/credits`, { credits: parseInt(credits) });
      toast.success(`Added ${credits} credits`);
      setCreditOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewHistory = async (adminId, adminName) => {
    try {
      const { data } = await API.get('/credits/history', { params: { admin_id: adminId } });
      setHistory(data.history || []);
      setHistoryAdmin(adminName);
      setHistoryOpen(true);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  // Admin's own credit view
  if (user?.role === 'admin' && mySummary) {
    return (
      <div className="space-y-6" data-testid="credit-management-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>💰 My Credits</h1>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <CreditCard emoji="💎" label="Balance" value={mySummary.balance} color="text-blue-500" />
          <CreditCard emoji="➕" label="Total Added" value={mySummary.total_added} color="text-green-500" />
          <CreditCard emoji="📤" label="Total Used" value={mySummary.total_used} color="text-orange-500" />
          <CreditCard emoji="🔄" label="Refunded" value={mySummary.total_refunded} color="text-violet-500" />
        </div>
        <Button variant="outline" onClick={() => viewHistory(user.id, user.name)} className="gap-2">View History</Button>
        <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} history={history} adminName={historyAdmin} />
      </div>
    );
  }

  // Super Admin credit management view
  return (
    <div className="space-y-6" data-testid="credit-management-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>💰 Credit Management</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage broadcast credits for admins</p>
      </div>

      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Admin</th>
              <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">💎 Balance</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">➕ Added</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">📤 Used</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">🔄 Refund</th>
              <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin, i) => {
              const s = summaries.find(x => x.admin_id === admin.id) || {};
              return (
                <tr key={admin.id} className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{admin.name}</div>
                    <div className="text-xs text-muted-foreground">{admin.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={admin.is_active ? 'default' : 'destructive'} className="text-xs">{admin.is_active ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right"><span className="font-mono text-lg font-bold">{admin.credits || 0}</span></td>
                  <td className="px-4 py-3 text-right font-mono text-green-600 dark:text-green-400">{s.total_added || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-orange-600 dark:text-orange-400">{s.total_used || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-violet-600 dark:text-violet-400">{s.total_refunded || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button data-testid={`add-credits-${admin.id}`} variant="outline" size="sm" onClick={() => openAddCredits(admin)} className="gap-1 h-7 text-xs">
                        <Plus size={12} /> Add
                      </Button>
                      <Button data-testid={`view-history-${admin.id}`} variant="ghost" size="sm" onClick={() => viewHistory(admin.id, admin.name)} className="h-7 text-xs">
                        History
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {admins.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No admins</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Credits Dialog */}
      <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Coins size={20} /> Add Credits</DialogTitle>
          </DialogHeader>
          <form onSubmit={addCredits} className="space-y-4">
            <p className="text-sm text-muted-foreground">Adding credits for <strong>{selectedAdmin?.name}</strong></p>
            <p className="text-sm">Current balance: <span className="font-mono font-bold text-lg">{selectedAdmin?.credits || 0}</span></p>
            <div className="space-y-1.5">
              <Label>Credits to Add</Label>
              <Input data-testid="credit-amount-input" type="number" min="1" value={credits} onChange={e => setCredits(e.target.value)} placeholder="100" required />
            </div>
            <DialogFooter>
              <Button data-testid="credit-submit-btn" type="submit">Add Credits</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} history={history} adminName={historyAdmin} />
    </div>
  );
}

function CreditCard({ emoji, label, value, color }) {
  return (
    <div className="border border-border rounded-lg p-4 hover:-translate-y-0.5 transition-all">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <span className="text-xl">{emoji}</span>
      </div>
      <p className={`font-mono text-2xl sm:text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function HistoryDialog({ open, onOpenChange, history, adminName }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Credit History - {adminName}</DialogTitle></DialogHeader>
        <div className="space-y-1">
          {history.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">No credit history</p>
          ) : (
            history.map(h => (
              <div key={h.id} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
                <div className="flex items-center gap-2">
                  {h.type === 'add' && <ArrowUp size={16} className="text-green-500" />}
                  {h.type === 'deduct' && <ArrowDown size={16} className="text-red-500" />}
                  {h.type === 'refund' && <ArrowCounterClockwise size={16} className="text-violet-500" />}
                  <div>
                    <span className="text-sm font-medium capitalize">{h.type}</span>
                    <span className="text-xs text-muted-foreground ml-2">{h.added_by_name}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`font-mono font-semibold ${h.type === 'add' || h.type === 'refund' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {h.type === 'add' || h.type === 'refund' ? '+' : ''}{h.amount}
                  </span>
                  <p className="text-[10px] font-mono text-muted-foreground">{h.created_at?.replace('T', ' ').slice(0, 16)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
